import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonProps,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaApple, FaGoogle, FaMicrosoft, FaYahoo } from 'react-icons/fa';
import { FiCalendar, FiDownload } from 'react-icons/fi';
import firebaseFirestore from '~/config/firebase/firestore';
import type { Company, Location, Reservation } from '~/core/types';
import {
  archivoIcs,
  descargarIcs,
  enlaceGoogle,
  enlaceOutlook,
  enlaceYahoo,
  eventoDeReserva,
  nombreArchivoIcs,
  resumenFecha,
  type EventoCalendario,
  type Idioma,
} from './eventoCalendario';

/**
 * El botón "Agregar a mi calendario" y su ventana.
 *
 * Solo aparece si la empresa lo tiene activado en su configuración. Cada
 * opción es un enlace o un archivo que se arma en el navegador: no hay
 * ninguna llamada a ningún servicio.
 *
 * El orden de las opciones depende del aparato: en iPhone primero Apple, en
 * Android primero Google. Es donde casi siempre acierta.
 */

type Props = {
  reservation: Reservation | undefined;
  reservationId: string;
  /**
   * La empresa, si quien pinta el botón ya la tiene. Si no, se resuelve desde
   * `reservation.location.company` (una lectura, una sola vez): es el caso de
   * la pantalla de modificar, que no la carga.
   */
  company?: Company;
  /** Cómo pintar el botón: se adapta al fondo de cada pantalla. */
  botonProps?: ButtonProps;
  /**
   * Idioma fijado desde fuera (la tarjeta reabierta desde el panel lo pone en
   * el de la reserva). Sin él, se deduce como siempre: el de la reserva si es
   * inglés, y si no el de la página.
   */
  idioma?: Idioma;
};

type Aparato = 'ios' | 'android' | 'otro';

const detectarAparato = (): Aparato => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS se hace pasar por Mac, pero es táctil.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'otro';
};

const abrir = (url: string) => {
  window.open(url, '_blank', 'noopener');
};

/**
 * Dentro de un iframe (el formulario va embebido en la web del cliente), la
 * descarga depende de cómo esté configurado ese iframe: si lleva `sandbox`
 * sin `allow-downloads`, el navegador la bloquea sin decir nada. No se puede
 * detectar desde dentro, así que se ofrece la salida: abrir la reserva en una
 * pestaña propia, donde la descarga sí funciona.
 */
const enIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const AgregarACalendario = ({
  reservation,
  reservationId,
  company,
  botonProps,
  idioma: idiomaFijado,
}: Props) => {
  const { t: tDePagina, i18n } = useTranslation();
  // Con idioma fijado, los textos no siguen al de la página: solo este botón.
  const t = idiomaFijado ? i18n.getFixedT(idiomaFijado) : tDePagina;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [sede, setSede] = useState<Location>();
  const [hermanas, setHermanas] = useState<{ id: string; datos: Reservation }[]>();
  const [descargado, setDescargado] = useState(false);
  const [empresaCargada, setEmpresaCargada] = useState<Company>();
  const empresa = company ?? empresaCargada;

  // Sin empresa a mano, se lee de la referencia que lleva la reserva.
  useEffect(() => {
    if (company || empresaCargada) return;
    const ref = (reservation?.location as { company?: unknown } | undefined)?.company;
    const esReferencia = Boolean(ref && typeof (ref as { path?: string }).path === 'string');
    if (!esReferencia) return;
    getDoc(ref as Parameters<typeof getDoc>[0])
      .then((s) => {
        if (s.exists()) setEmpresaCargada({ id: s.id, ...(s.data() as Omit<Company, 'id'>) });
      })
      .catch(() => undefined);
  }, [company, empresaCargada, reservation]);

  const activo = Boolean(empresa?.settings?.calendar?.active);
  const idioma: Idioma =
    idiomaFijado ??
    ((reservation as { lang?: string } | undefined)?.lang === 'en'
      ? 'en'
      : i18n.language?.startsWith('en')
      ? 'en'
      : 'es');

  // Lo que hace falta pedir, solo al abrir y solo si hace falta:
  //  · la sede completa, cuando la reserva no trae hora de fin (para saber
  //    cuándo cierra);
  //  · las demás fechas de la serie, cuando la reserva se repite.
  useEffect(() => {
    if (!isOpen || !reservation) return;
    setDescargado(false);

    const idSede = reservation.location?.id;
    if (!reservation.endDatetime && idSede && !sede) {
      getDoc(doc(firebaseFirestore, 'locations', idSede))
        .then((s) => {
          if (s.exists()) setSede({ id: s.id, ...(s.data() as Omit<Location, 'id'>) });
        })
        .catch(() => undefined);
    }

    const serie = (reservation as { recurrence?: { id?: string } }).recurrence?.id;
    if (serie && !hermanas) {
      getDocs(query(collection(firebaseFirestore, 'reservations'), where('recurrence.id', '==', serie)))
        .then((snap) => {
          setHermanas(
            snap.docs
              .map((d) => ({ id: d.id, datos: d.data() as Reservation }))
              .filter((r) => r.datos.status !== 'cancelled')
              .sort(
                (a, b) =>
                  ((a.datos.startDatetime as { toMillis?: () => number })?.toMillis?.() ?? 0) -
                  ((b.datos.startDatetime as { toMillis?: () => number })?.toMillis?.() ?? 0)
              )
          );
        })
        .catch(() => setHermanas([]));
    }
  }, [isOpen, reservation, sede, hermanas]);

  const origen = window.location.origin;

  const evento = useMemo<EventoCalendario | null>(() => {
    if (!reservation || !empresa) return null;
    return eventoDeReserva({ reserva: reservation, reservaId: reservationId, empresa, sede, idioma, origen });
  }, [reservation, reservationId, empresa, sede, idioma, origen]);

  /** Todos los eventos que van al archivo: la serie entera, o solo este. */
  const eventos = useMemo<EventoCalendario[]>(() => {
    if (!evento || !empresa || !reservation) return [];
    if (!hermanas || hermanas.length <= 1) return [evento];
    return hermanas
      .map((h) => eventoDeReserva({ reserva: h.datos, reservaId: h.id, empresa, sede, idioma, origen }))
      .filter((e): e is EventoCalendario => e !== null);
  }, [evento, hermanas, empresa, reservation, sede, idioma, origen]);

  if (!activo || !reservation || !evento) return null;

  const aparato = detectarAparato();
  const descargar = () => {
    descargarIcs(nombreArchivoIcs(eventos, reservation.code), archivoIcs(eventos));
    setDescargado(true);
  };

  const opciones = [
    {
      clave: 'google',
      icono: FaGoogle,
      texto: t('reserve_confirmation.calendar_google'),
      color: '#4285F4',
      accion: () => abrir(enlaceGoogle(evento)),
    },
    {
      clave: 'apple',
      icono: FaApple,
      texto: t('reserve_confirmation.calendar_apple'),
      color: '#111',
      accion: descargar,
    },
    {
      clave: 'outlook',
      icono: FaMicrosoft,
      texto: t('reserve_confirmation.calendar_outlook'),
      color: '#0078D4',
      accion: () => abrir(enlaceOutlook(evento, 'live')),
    },
    {
      clave: 'office',
      icono: FaMicrosoft,
      texto: t('reserve_confirmation.calendar_office'),
      color: '#D83B01',
      accion: () => abrir(enlaceOutlook(evento, 'office')),
    },
    {
      clave: 'yahoo',
      icono: FaYahoo,
      texto: t('reserve_confirmation.calendar_yahoo'),
      color: '#6001D2',
      accion: () => abrir(enlaceYahoo(evento)),
    },
  ];

  // En iPhone, Apple primero; en Android, Google (ya lo está).
  const ordenadas =
    aparato === 'ios' ? [opciones[1], ...opciones.filter((o) => o.clave !== 'apple')] : opciones;

  const esSerie = eventos.length > 1;

  return (
    <>
      <Button leftIcon={<Icon as={FiCalendar} />} onClick={onOpen} {...botonProps}>
        {t('reserve_confirmation.calendar_add')}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size='sm' isCentered scrollBehavior='inside'>
        <ModalOverlay backdropFilter='blur(2px)' />
        <ModalContent>
          <ModalHeader pb={1}>
            <HStack spacing={2}>
              <Icon as={FiCalendar} color='blue.500' />
              <Text fontSize='md'>{t('reserve_confirmation.calendar_add')}</Text>
            </HStack>
            <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
              {evento.titulo} · {resumenFecha(evento, empresa?.settings?.calendar?.timezone)}
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={5}>
            {evento.cancelado && (
              <Alert status='warning' borderRadius='md' fontSize='sm' mb={3} py={2}>
                <AlertIcon boxSize={4} />
                {t('reserve_confirmation.calendar_cancelled')}
              </Alert>
            )}
            {esSerie && (
              <Alert status='info' borderRadius='md' fontSize='sm' mb={3} py={2}>
                <AlertIcon boxSize={4} />
                {t('reserve_confirmation.calendar_series', { count: eventos.length })}
              </Alert>
            )}

            <Text fontSize='xs' color='gray.500' mb={2} textTransform='uppercase' letterSpacing='wide'>
              {t('reserve_confirmation.calendar_pick')}
            </Text>

            <VStack align='stretch' spacing={2}>
              {ordenadas.map((o) => (
                <Button
                  key={o.clave}
                  variant='outline'
                  justifyContent='flex-start'
                  h='46px'
                  leftIcon={<Icon as={o.icono} color={o.color} boxSize={4} />}
                  onClick={o.accion}
                  fontWeight='medium'
                  _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                >
                  {o.texto}
                </Button>
              ))}

              <Box pt={2}>
                <Button
                  variant='ghost'
                  size='sm'
                  w='full'
                  justifyContent='flex-start'
                  leftIcon={<Icon as={FiDownload} />}
                  onClick={descargar}
                  color='gray.600'
                >
                  {t('reserve_confirmation.calendar_ics')}
                </Button>
                <Text fontSize='xs' color='gray.400' pl={3}>
                  {t('reserve_confirmation.calendar_ics_hint')}
                </Text>
              </Box>

              {descargado && (
                <Alert status='success' borderRadius='md' fontSize='sm' py={2} alignItems='flex-start'>
                  <AlertIcon boxSize={4} mt={0.5} />
                  <Box>
                    {t('reserve_confirmation.calendar_downloaded')}
                    {enIframe() && (
                      <Text
                        as='a'
                        href={evento.url}
                        target='_blank'
                        rel='noopener'
                        display='block'
                        mt={1}
                        fontSize='xs'
                        color='blue.600'
                        textDecoration='underline'
                      >
                        {t('reserve_confirmation.calendar_iframe_hint')}
                      </Text>
                    )}
                  </Box>
                </Alert>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AgregarACalendario;
